import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { dataLayer } from '../../../data/dataLayer';
import { X, Activity, ArrowUpRight, ArrowDownRight, ArrowRight, Clock, Target, Zap, CheckCircle2, AlertTriangle, UserMinus, Unlink, StickyNote, BarChart3, ClipboardCheck } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { type ModuleId, MODULES, getModuleIcon } from '../../../content';
import { GroupId } from '../../../config/systemConfig';
import { getRecentSessionsForLearner, getAllModuleTrends, getWeeklyActivityForLearner, getWeeklyStats } from '../../../utils/activityHelpers';
import { getDashboardStats } from '../../../utils/dashboardStats';
import { ActivityTimeline } from '../../../components/shared/ActivityTimeline';
import { WeeklyActivityDots } from '../../../components/shared/WeeklyActivityDots';
import { DjayNarrator } from '../../../mascot';
import { InsightBar } from '../components/InsightBar';
import { AppState } from '../../../app/types';
import { useFocusTrap } from '../../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { getAssessmentHistoryForLearner, getReport, type AssessmentHistoryEntry, type AssessmentReport } from '../../../assessment';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { getStudentInsights } from '../utils/differentiationInsights';
// ============================================================
// Weekly accuracy trend helper (SD-1)
// ============================================================
interface WeekAccuracy {
  label: string;
  accuracy: number;
  questionCount: number;
}
function getWeeklyAccuracyTrend(questionStates: Record<string, {
  history?: Array<{
    timestamp: number;
    isCorrect: boolean;
  }>;
}>, weeks: number = 4): WeekAccuracy[] {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const thisMonday = new Date(now);
  thisMonday.setHours(0, 0, 0, 0);
  thisMonday.setDate(thisMonday.getDate() + mondayOffset);
  const thisMondayTs = thisMonday.getTime();
  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  const weekLabels = ['4w geleden', '3w geleden', '2w geleden', 'Vorige week', 'Deze week'];
  const result: WeekAccuracy[] = [];
  for (let i = weeks; i >= 0; i--) {
    const weekStart = thisMondayTs - i * MS_PER_WEEK;
    const weekEnd = weekStart + MS_PER_WEEK;
    let correct = 0;
    let total = 0;
    for (const state of Object.values(questionStates)) {
      if (!state.history) continue;
      for (const attempt of state.history) {
        if (attempt.timestamp >= weekStart && attempt.timestamp < weekEnd) {
          total++;
          if (attempt.isCorrect) correct++;
        }
      }
    }
    result.push({
      label: weekLabels[weeks - i] || `${i}w`,
      accuracy: total > 0 ? Math.round(correct / total * 100) : 0,
      questionCount: total
    });
  }
  return result;
}
export interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface StudentDetailPanelProps {
  student: StudentData;
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onClose: () => void;
  onViewDashboard?: (learnerId: string) => void;
  isOpen: boolean;
  classroomName?: string;
  onRemoveFromClassroom?: (studentId: string) => void;
  onUnlinkStudent?: (studentId: string) => void;
  onNavigateToAssessments?: (studentName: string) => void;
}
export function StudentDetailPanel({
  student,
  selectedGroup,
  getModuleMastery,
  onClose,
  onViewDashboard,
  isOpen,
  classroomName,
  onRemoveFromClassroom,
  onUnlinkStudent,
  onNavigateToAssessments
}: StudentDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer'>('overzicht');
  const [confirmAction, setConfirmAction] = useState<'remove' | 'unlink' | null>(null);
  const [note, setNote] = useState('');
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null);
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [assessmentReports, setAssessmentReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  /** AS-2026-07-P3-02: distinguish a failed load from "no toetsen yet". */
  const [assessmentLoadError, setAssessmentLoadError] = useState(false);
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose);
  const stats = useMemo(() => {
    return getDashboardStats(student.data, MODULES, selectedGroup);
  }, [student.data, selectedGroup]);
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const weeklyStats = useMemo(() => {
    return getWeeklyStats(student.data.questionStates || {});
  }, [student.data.questionStates]);
  const moduleMastery = useMemo(() => {
    return MODULES.map(module => {
      const mastery = getModuleMastery(student.id, module.id);
      return {
        module,
        mastery
      };
    }).sort((a, b) => b.mastery - a.mastery);
  }, [student.id, getModuleMastery]);
  const recentSessions = useMemo(() => {
    return getRecentSessionsForLearner(student.data.questionStates || {}, 5);
  }, [student.data.questionStates]);
  const trends = useMemo(() => {
    return getAllModuleTrends(student.data.familyMastery || {}, selectedGroup);
  }, [student.data.familyMastery, selectedGroup]);
  const strengths = moduleMastery.filter(m => m.mastery >= 60).slice(0, 3);
  const weaknesses = [...moduleMastery].reverse().filter(m => m.mastery > 0 && m.mastery < 40).slice(0, 3);
  const differentiationInsights = useMemo(() => {
    return getStudentInsights(student.data, selectedGroup, 3);
  }, [student.data, selectedGroup]);
  const practicedModules = moduleMastery.filter(m => m.mastery > 0);
  const overallMastery = practicedModules.length > 0 ? Math.round(practicedModules.reduce((sum, m) => sum + m.mastery, 0) / practicedModules.length) : 0;
  // Determine Djay's message and emotion
  const getDjayInsight = () => {
    if (differentiationInsights.length > 0) {
      const primaryInsight = differentiationInsights[0];
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} loopt vast bij ${primaryInsight.familyName.toLowerCase()}. ${primaryInsight.recommendation}`
      };
    }
    if (weaknesses.length > 0) {
      return {
        emotion: 'encouraging' as const,
        message: `${student.name} heeft wat extra oefening nodig bij ${weaknesses[0].module.title.toLowerCase()}.`
      };
    }
    if (strengths.length > 0 && overallMastery > 70) {
      return {
        emotion: 'proud' as const,
        message: `Wauw, ${student.name} is ontzettend goed bezig! Vooral ${strengths[0].module.title.toLowerCase()} gaat fantastisch.`
      };
    }
    if (weeklyStats.daysPracticed >= 3) {
      return {
        emotion: 'happy' as const,
        message: `${student.name} heeft een goed oefenritme te pakken deze week!`
      };
    }
    return {
      emotion: 'thinking' as const,
      message: `Laten we samen kijken hoe we ${student.name} verder kunnen helpen groeien.`
    };
  };
  const djayInsight = getDjayInsight();
  // Load notes on mount or student change
  useEffect(() => {
    dataLayer.getTeacherNote(student.id).then(setNote).catch(() => setNote(''));
  }, [student.id]);
  const handleNoteBlur = () => {
    setSaveStatus('saving');
    dataLayer.saveTeacherNote(student.id, note).then(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }).catch(() => {
      setSaveStatus(null);
    });
  };
  // Reset confirmation state when panel closes or student changes
  useEffect(() => {
    setConfirmAction(null);
    if (!isOpen) {
      setActiveTab('overzicht');
    }
  }, [isOpen, student.id]);
  useEffect(() => {
    if (activeTab === 'toets') {
      setIsLoadingAssessments(true);
      setAssessmentLoadError(false);
      // AS-2026-07-P1-01: learner scope enforced by the accessor rather than
      // an ad-hoc filter at this call-site.
      getAssessmentHistoryForLearner(student.id, selectedGroup).then(filteredHistory => {
        setAssessmentHistory(filteredHistory);
        Promise.all(filteredHistory.map(entry => getReport(entry.attemptId).then(report => ({
          id: entry.attemptId,
          report
        })))).then(results => {
          const reportsMap: Record<string, AssessmentReport> = {};
          results.forEach(({
            id,
            report
          }) => {
            if (report) reportsMap[id] = report;
          });
          setAssessmentReports(reportsMap);
          setIsLoadingAssessments(false);
        }).catch(() => {
          // AS-2026-07-P3-02: reports failed — history is still valid, but
          // flag it so the panel does not imply "no results".
          setAssessmentLoadError(true);
          setIsLoadingAssessments(false);
        });
      }).catch(() => {
        setAssessmentLoadError(true);
        setIsLoadingAssessments(false);
      });
    }
  }, [activeTab, selectedGroup, student.id]);
  const TABS: {
    id: 'overzicht' | 'vakken' | 'activiteit' | 'toets' | 'beheer';
    label: string;
  }[] = [{
    id: 'overzicht',
    label: 'Overzicht'
  }, {
    id: 'vakken',
    label: 'Vakken'
  }, {
    id: 'activiteit',
    label: 'Activiteit'
  }, {
    id: 'toets',
    label: 'Toetsen'
  }, {
    id: 'beheer',
    label: 'Beheer'
  }];
  return <AnimatePresence data-id="element-10117" data-mp="5fd5e9xliq62">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40" data-id="element-10118" data-mp="g0ijp6q5j3h" />

          {/* Slide-over Panel */}
          <motion.div ref={focusTrapRef as React.RefObject<HTMLDivElement>} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-2xl bg-slate-50 shadow-2xl z-50 flex flex-col border-l border-slate-200 overflow-hidden" data-id="element-10119" data-mp="3isg7ftc73t5o">
            {/* Header */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-10120" data-mp="1jdenbn1dojc9">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-10121" data-mp="1ochpmzpz4bgj">
                {student.name}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-10122" data-mp="vpx1070vudgo">
                <X className="w-5 h-5" data-id="element-10123" data-mp="15n1hbq81repk" />
              </button>
            </div>

            {/* Tab Bar (Sticky) */}
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 flex items-center gap-6 sticky top-0 z-10" data-id="element-10124" data-mp="1afumgf7fgp26">
              {TABS.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-4 text-sm font-bold transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-sm ${activeTab === tab.id ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-10125" data-mp="hx73b5bzrk3q">
                  {tab.label}
                  {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" initial={false} data-id="element-10126" data-mp="eqludr7z1meq" />}
                </button>)}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 relative" data-id="element-10127" data-mp="3s24b8bm5cit5">
              <AnimatePresence mode="wait" data-id="element-10128" data-mp="1cbrk0lk52843">
                {activeTab === 'overzicht' && <motion.div key="overzicht" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-8" data-id="element-10129" data-mp="17cx17jm68jjz">
                    {/* Student Profile Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-10130" data-mp="3d0dyximv1ps0">
                      <div className="flex items-center gap-5" data-id="element-10131" data-mp="2z9iymuqfgh01">
                        <div className="relative" data-id="element-10132" data-mp="347ua19wdezkd">
                          <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-3xl font-black shadow-md transform -rotate-3" data-id="element-10133" data-mp="346j4p5na1jr5">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-sm border border-slate-100" data-id="element-10134" data-mp="282twqgdmvhqg">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center border-2 border-white" data-id="element-10135" data-mp="231o0w9oofecp">
                              <span className="text-xs font-black text-emerald-700" data-id="element-10136" data-mp="1u2j2r4h11n12">
                                {overallMastery}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-10137" data-mp="2mrq8ooit9ewc">
                          <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-id="element-10138" data-mp="2i0i2008v87mv">
                            {student.name}
                          </h1>
                          <div className="flex items-center gap-3 mt-1.5" data-id="element-10139" data-mp="2d0vlnhxqqb9u">
                            <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg text-sm font-bold border border-slate-200" data-id="element-10140" data-mp="3ahud2y3tpioe">
                              Groep {selectedGroup}
                            </span>
                            {onViewDashboard && <button onClick={() => onViewDashboard(student.id)} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-10141" data-mp="3hzsa434tj1aq">
                                Bekijk als leerling{' '}
                                <ArrowRight className="w-3.5 h-3.5" data-id="element-10142" data-mp="3vzta7gd9e4p5" />
                              </button>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Djay Insight */}
                    <div className="flex items-center gap-4 bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100" data-id="element-10143" data-mp="ej207d64843r">
                      <DjayNarrator size="sm" emotion={djayInsight.emotion} data-id="element-10144" data-mp="ij3rqti4zh5b" />
                      <p className="text-sm font-medium text-indigo-900 leading-relaxed" data-id="element-10145" data-mp="nkt1m5tmhort">
                        {djayInsight.message}
                      </p>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-10146" data-mp="2fjztxarequ8s">
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10147" data-mp="2teiqv6ol15jp">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2" data-id="element-10148" data-mp="2okejwnfbgxim">
                          <CheckCircle2 className="w-4 h-4" data-id="element-10149" data-mp="2yctyrbvbh5za" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10150" data-mp="3cbto6lqnlowi">
                          {stats.totalQuestions}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10151" data-mp="37digohntzqhg">
                          Beantwoord
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10152" data-mp="1d0sjnr26215u">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2" data-id="element-10153" data-mp="3ooe4sh7vw6e0">
                          <Activity className="w-4 h-4" data-id="element-10154" data-mp="3fuj0tcii0lzy" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10155" data-mp="3jvkz9r4lcu0v">
                          {Math.round(stats.accuracy * 100)}%
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10156" data-mp="35uidzglf7nr0">
                          Nauwkeurigheid
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10157" data-mp="2wx84o0s5y8oq">
                        <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2" data-id="element-10158" data-mp="30yjsq1ve2lut">
                          <Zap className="w-4 h-4" data-id="element-10159" data-mp="z5vl7gbppc98" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10160" data-mp="3j14vqu5q01bu">
                          {stats.currentStreak}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10161" data-mp="3e2tkqg3ovks1">
                          Dagen Streak
                        </div>
                      </Card>
                      <Card className="p-4 border-slate-200 shadow-sm flex flex-col items-center text-center" data-id="element-10162" data-mp="34c8eq7lordz3">
                        <div className="w-8 h-8 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center mb-2" data-id="element-10163" data-mp="308twrbe6snj0">
                          <Clock className="w-4 h-4" data-id="element-10164" data-mp="2qch7qqtl6xul" />
                        </div>
                        <div className="text-xl font-black text-slate-900" data-id="element-10165" data-mp="2ldnqqof3copo">
                          {stats.learningTime}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-10166" data-mp="ok2lxgnx998w">
                          Minuten
                        </div>
                      </Card>
                    </div>

                    {/* SD-1: Weekly Accuracy Trend */}
                    {(() => {
                const weeklyTrend = getWeeklyAccuracyTrend(student.data.questionStates || {});
                const hasData = weeklyTrend.some(w => w.questionCount > 0);
                if (!hasData) return null;
                return <Card className="p-5 border-slate-200 shadow-sm bg-white" data-id="element-10167" data-mp="d595wtzyx8zm">
                          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10168" data-mp="i4ko87fzgmjb">
                            <BarChart3 className="w-4 h-4 text-indigo-500" data-id="element-10169" data-mp="n500evoj3nzp" />
                            Trend afgelopen weken
                          </h3>
                          <div className="flex items-end gap-2 h-28" data-id="element-10170" data-mp="1yj3s1v7n901l">
                            {weeklyTrend.map((week, i) => {
                      const barHeight = week.questionCount > 0 ? Math.max(week.accuracy / 100 * 100, 8) : 0;
                      const barColor = week.accuracy >= 70 ? 'bg-emerald-500' : week.accuracy >= 40 ? 'bg-amber-400' : week.questionCount > 0 ? 'bg-rose-400' : 'bg-slate-200';
                      return <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-10171" data-mp="35fci3x5esp81" data-mpi={i}>
                                  <span className="text-[10px] font-bold text-slate-600 tabular-nums" data-id="element-10172" data-mp="ko3nrvqviwem">
                                    {week.questionCount > 0 ? `${week.accuracy}%` : '—'}
                                  </span>
                                  <div className="w-full flex items-end justify-center h-20" data-id="element-10173" data-mp="1wdkesqbtpsl1">
                                    <motion.div initial={{
                            height: 0
                          }} animate={{
                            height: `${barHeight}%`
                          }} transition={{
                            duration: 0.6,
                            delay: i * 0.08,
                            ease: 'easeOut'
                          }} className={`w-full max-w-[40px] rounded-t-md ${barColor}`} data-id="element-10174" data-mp="1reg57ykzlsu6" />
                                  </div>
                                  <span className="text-[9px] font-bold text-slate-400 text-center leading-tight" data-id="element-10175" data-mp="3qqj9d7z6x0ke">
                                    {week.label}
                                  </span>
                                </div>;
                    })}
                          </div>
                        </Card>;
              })()}

                    {/* Strengths & Weaknesses */}
                    {(strengths.length > 0 || differentiationInsights.length > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-10176" data-mp="3vrgn98d638g3">
                        {strengths.length > 0 && <Card className="p-5 border-emerald-100 bg-emerald-50/30 shadow-sm" data-id="element-10177" data-mp="2k9e6ebgyz39b">
                            <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10178" data-mp="2p9jvbjvuubnp">
                              <ArrowUpRight className="w-4 h-4" data-id="element-10179" data-mp="2hny37b9rnykj" /> Sterk in
                            </h3>
                            <div className="space-y-2.5" data-id="element-10180" data-mp="2cq4zcovxrgnf">
                              {strengths.map(s => {
                      const IconComp = getModuleIcon(s.module.icon);
                      return <div key={s.module.id} className="flex items-center justify-between bg-white px-3 py-2.5 rounded-xl border border-emerald-100 shadow-sm text-sm" data-id="element-10181" data-mp="1y1ib1xobzek2">
                                    <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-10182" data-mp="27t694hfe7tui">
                                      <IconComp className="w-4 h-4 text-emerald-500" data-id="element-10183" data-mp="1fb0tmp8y85e0" />
                                      <span className="truncate" data-id="element-10184" data-mp="1f8o3hot1eopv">
                                        {s.module.title}
                                      </span>
                                    </div>
                                    <span className="font-black text-emerald-700 ml-2" data-id="element-10185" data-mp="1sxr7fguve2me">
                                      {s.mastery}%
                                    </span>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                        {differentiationInsights.length > 0 && <Card className="p-5 border-rose-100 bg-rose-50/30 shadow-sm" data-id="element-10186" data-mp="2bn7njcj1g3nx">
                            <h3 className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-10187" data-mp="2gn2chaj96j6c">
                              <AlertTriangle className="w-4 h-4" data-id="element-10188" data-mp="2llndhc6bx6vu" />{' '}
                              Differentiatie Inzicht
                            </h3>
                            <div className="space-y-3" data-id="element-10189" data-mp="r7vxzw1bjssh">
                              {differentiationInsights.map(insight => {
                      const moduleDef = MODULES.find(m => m.id === insight.moduleId);
                      const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
                      return <div key={insight.familyId} className="bg-white p-3 rounded-xl border border-rose-100 shadow-sm text-sm flex flex-col gap-2" data-id="element-10190" data-mp="3nht4diqri9tg">
                                    <div className="flex items-center justify-between" data-id="element-10191" data-mp="39hqo20y3rogb">
                                      <div className="flex items-center gap-2.5 text-slate-700 font-medium" data-id="element-10192" data-mp="39k4ge21eemk6">
                                        <IconComp className="w-4 h-4 text-rose-500" data-id="element-10193" data-mp="1y2l99ykp5zgc" />
                                        <span className="truncate font-bold" data-id="element-10194" data-mp="1y4y0l2cchm62">
                                          {insight.familyName}
                                        </span>
                                      </div>
                                      <span className="font-black text-rose-600 ml-2" data-id="element-10195" data-mp="1qjls7zkpkk05">
                                        {insight.mastery}%
                                      </span>
                                    </div>
                                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-10196" data-mp="1gshet4aol77f">
                                      {insight.recommendation}
                                    </p>
                                  </div>;
                    })}
                            </div>
                          </Card>}
                      </div>}
                  </motion.div>}

                {activeTab === 'vakken' && <motion.div key="vakken" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-10197" data-mp="12vkpx8e4hzt6">
                    {/* Module Mastery List */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-10198" data-mp="x6xw3zejv3ge">
                      <div className="p-5 border-b border-slate-100 bg-white" data-id="element-10199" data-mp="1bvc0wfvj11zl">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-10200" data-mp="16wqzwe8gaea3">
                          <Target className="w-5 h-5 text-indigo-500" data-id="element-10201" data-mp="1kwa4ix21z7hn" />
                          Vakbeheersing
                        </h3>
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-10202" data-mp="2naehywu5r6lg">
                        {practicedModules.length > 0 ? practicedModules.map(({
                    module,
                    mastery
                  }) => {
                    const trend = trends[module.id];
                    const IconComp = getModuleIcon(module.icon);
                    const barColor = mastery >= 60 ? 'bg-emerald-500' : mastery >= 40 ? 'bg-amber-400' : 'bg-rose-500';
                    const textColor = mastery >= 60 ? 'text-emerald-700' : mastery >= 40 ? 'text-amber-600' : 'text-rose-600';
                    return <div key={module.id} className="p-4 hover:bg-slate-50 transition-colors flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-10203" data-mp="1pth2h919guzt">
                                <div className="flex items-center gap-3 sm:w-1/3 flex-shrink-0" data-id="element-10204" data-mp="19aku10ni02kv">
                                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-10205" data-mp="14c01lnehmn6z">
                                    <IconComp className="w-4 h-4 text-slate-500" data-id="element-10206" data-mp="zdoql9cgi6n6" />
                                  </div>
                                  <div className="font-bold text-slate-800 text-sm truncate" data-id="element-10207" data-mp="uklx117oix6z">
                                    {module.title}
                                  </div>
                                </div>

                                <div className="flex-1 flex items-center gap-4" data-id="element-10208" data-mp="ljfekmlgzcb3">
                                  <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-10209" data-mp="gldz0bn95v4d">
                                    <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${mastery}%`
                          }} transition={{
                            duration: 0.8,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${barColor}`} data-id="element-10210" data-mp="bp5iouxbjvvm" />
                                  </div>
                                  <div className={`w-10 text-right font-black text-sm ${textColor}`} data-id="element-10211" data-mp="37rse446urbkn">
                                    {mastery}%
                                  </div>
                                  <div className="w-20 flex justify-end" data-id="element-10212" data-mp="2a9sauhbxjdnb">
                                    {trend === 'up' && <span className="text-[10px] font-bold text-emerald-700 flex items-center bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10213" data-mp="pqpuj9h6vht8">
                                        <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-10214" data-mp="gtfktsk0s0qf" />{' '}
                                        Stijgt
                                      </span>}
                                    {trend === 'down' && <span className="text-[10px] font-bold text-rose-600 flex items-center bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10215" data-mp="1dh2an7ml8kuw">
                                        <ArrowDownRight className="w-3 h-3 mr-1" data-id="element-10216" data-mp="14h6uspi381oj" />{' '}
                                        Daalt
                                      </span>}
                                    {trend === 'stable' && <span className="text-[10px] font-bold text-slate-400 flex items-center bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-10217" data-mp="3p5bepbske2iu">
                                        <ArrowRight className="w-3 h-3 mr-1" data-id="element-10218" data-mp="1pswbhvqj2kkb" />{' '}
                                        Stabiel
                                      </span>}
                                  </div>
                                </div>
                              </div>;
                  }) : <div className="p-8 text-center text-slate-500" data-id="element-10219" data-mp="170k1cmtmuezm">
                            Nog geen vakken geoefend.
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'activiteit' && <motion.div key="activiteit" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10220" data-mp="2sfhfjzadaej3">
                    {/* Recent Activity */}
                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-10221" data-mp="8gwuhpbmjsx">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-10222" data-mp="48znw9je3lu2">
                        <Activity className="w-5 h-5 text-indigo-500" data-id="element-10223" data-mp="98lz6dfnqs08" />
                        Activiteit deze week
                      </h3>
                      {weeklyStats.daysPracticed > 0 ? <div className="flex justify-center" data-id="element-10224" data-mp="1kmpr7e2oq62n">
                          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="indigo" data-id="element-10225" data-mp="1j6c14r20v91a" />
                        </div> : <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-10226" data-mp="1s7a3z60q5aia">
                          <DjayNarrator size="sm" emotion="sleeping" data-id="element-10227" data-mp="1o4djf9rmj4bp" />
                          <p className="text-sm font-medium text-slate-500 mt-3" data-id="element-10228" data-mp="224y285xjh057">
                            Nog geen activiteit voor deze leerling
                          </p>
                        </div>}
                    </Card>

                    <Card className="p-6 border-slate-200 shadow-sm" data-id="element-10229" data-mp="2bu7txkoh0gss">
                      <h3 className="text-base font-bold text-slate-900 mb-5 flex items-center gap-2" data-id="element-10230" data-mp="26wzc5l78ies2">
                        <Clock className="w-5 h-5 text-indigo-500" data-id="element-10231" data-mp="2kwqshsl84798" />
                        Laatste sessies
                      </h3>
                      {recentSessions.length > 0 ? <ActivityTimeline sessions={recentSessions} data-id="element-10232" data-mp="2zjftjyv4ishq" /> : <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-10233" data-mp="34hr0o1u1aow9">
                          <DjayNarrator size="sm" emotion="sleeping" data-id="element-10234" data-mp="2qgofdrav5ime" />
                          <p className="text-sm font-medium text-slate-500 mt-3" data-id="element-10235" data-mp="2uj1l4fpm1gj6">
                            Nog geen sessies gevonden
                          </p>
                        </div>}
                    </Card>
                  </motion.div>}

                {activeTab === 'toets' && <motion.div key="toets" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-10236" data-mp="26v1t7cgkbk30">
                    {/* Insight Bar */}
                    <InsightBar message={assessmentHistory.length === 0 ? `${student.name} heeft nog geen toetsen gemaakt.` : `${student.name} heeft ${assessmentHistory.length} toets(en) gemaakt. Resultaten geven een indicatie van het niveau op basis van de Meester djay taxonomie.`} data-id="element-10237" data-mp="3us19ek7kol5e" />

                    <Card className="border-slate-200 shadow-sm overflow-hidden" data-id="element-10238" data-mp="ibxogx53krbl">
                      <div className="p-5 border-b border-slate-100 bg-white flex items-center justify-between" data-id="element-10239" data-mp="daqh2z6v9u5g">
                        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-10240" data-mp="32xz6o2527alc">
                          <ClipboardCheck className="w-5 h-5 text-indigo-500" data-id="element-10241" data-mp="2y3lk8p8dkdst" />
                          Toetsresultaten
                        </h3>
                        {onNavigateToAssessments && assessmentHistory.length > 0 && <Button variant="outline" size="sm" onClick={() => onNavigateToAssessments(student.name)} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-id="element-10242" data-mp="1mj37tcejcn2w">
                              <ArrowRight className="w-4 h-4 mr-2" data-id="element-10243" data-mp="2696tvq9bvvim" />
                              Bekijk alle toetsen
                            </Button>}
                      </div>

                      <div className="divide-y divide-slate-100 bg-white" data-id="element-10244" data-mp="bvfd86sl0bdo">
                        {isLoadingAssessments ? <div className="p-8 text-center text-slate-500" data-id="element-10245" data-mp="fx99m2vqut81">
                            Laden...
                          </div> : assessmentLoadError && assessmentHistory.length === 0 ? (/* AS-2026-07-P3-02: failed read ≠ "geen toetsen". */
                  <div className="p-8 text-center" role="alert" data-id="element-10246" data-mp="1pntldlnpmir">
                            <p className="font-bold text-slate-900 mb-1" data-id="element-10247" data-mp="3a5pqvvf8enga">
                              Toetsgegevens konden niet geladen worden
                            </p>
                            <p className="text-sm text-slate-500" data-id="element-10248" data-mp="358pkfjegzion">
                              Er is niets verwijderd. Open het tabblad opnieuw
                              om het nog eens te proberen.
                            </p>
                          </div>) : assessmentHistory.length > 0 ? assessmentHistory.map(entry => {
                    const report = assessmentReports[entry.attemptId];
                    return <div key={entry.attemptId} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-10249" data-mp="120gr955m9pgo">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-10250" data-mp="wyzu9kq96n5d">
                                  <div data-id="element-10251" data-mp="1ko29klxf03wp">
                                    <div className="font-bold text-slate-900" data-id="element-10252" data-mp="1kp3vjlfyyat7">
                                      {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-1" data-id="element-10253" data-mp="28fhgyt41tbet">
                                      {entry.totalQuestions} vragen •{' '}
                                      {Math.round(entry.totalTimeSeconds / 60)}{' '}
                                      minuten
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4" data-id="element-10254" data-mp="334l91owhlh4w">
                                    <div className="text-right" data-id="element-10255" data-mp="3qsmf8r670lh5">
                                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10256" data-mp="3qviceda7v64j">
                                        Score
                                      </div>
                                      <div className={`text-xl font-black ${entry.score >= 70 ? 'text-emerald-600' : entry.score >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-10257" data-mp="3h1ia0j6bkezi">
                                        {entry.score}%
                                      </div>
                                    </div>
                                    {report && <div className="text-right" data-id="element-10258" data-mp="nphw7igc9rou">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10259" data-mp="11hobz3fkxwnr">
                                          Niveau
                                        </div>
                                        <div className="flex items-center justify-end gap-2" data-id="element-10260" data-mp="8sqs4vvpzrsw">
                                          <div className="text-xl font-black text-indigo-600" data-id="element-10261" data-mp="4rym0bi5a30u">
                                            {report.overallEstimatedLevelCode}
                                          </div>
                                          {(() => {
                                const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
                                if (!cito) return null;
                                return <div className={`text-xs font-bold px-2 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-10262" data-mp="21bo0ydlcdpa9">
                                                {cito.label}
                                              </div>;
                              })()}
                                        </div>
                                      </div>}
                                  </div>
                                </div>

                                {report && report.byModule && report.byModule.length > 0 && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-10263" data-mp="s91l6v67g3kn">
                                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-10264" data-mp="nahtsf8udzkw">
                                        Per Vak
                                      </h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-10265" data-mp="dck7hxajmcux">
                                        {report.byModule.map(m => {
                            const moduleInfo = MODULES.find(mod => mod.id === m.moduleId);
                            if (!moduleInfo) return null;
                            const IconComp = getModuleIcon(moduleInfo.icon);
                            const cito = levelCodeToCitoLevel(m.estimatedLevelCode ?? '', report.selectedGroup);
                            return <div key={m.moduleId} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3" data-id="element-10266" data-mp="1i5qifgb3bui">
                                              <div className="flex items-center gap-2" data-id="element-10267" data-mp="3sqjgesaqfzoz">
                                                <IconComp className="w-4 h-4 text-slate-400" data-id="element-10268" data-mp="3eqiklr9brigd" />
                                                <span className="text-sm font-bold text-slate-700" data-id="element-10269" data-mp="3iqka721sclfa">
                                                  {moduleInfo.title}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-3" data-id="element-10270" data-mp="31hzws0l92897">
                                                <span className="text-sm font-black text-slate-900" data-id="element-10271" data-mp="31fnhjv6vmbzu">
                                                  {Math.round(m.scorePercentage ?? m.accuracy)}
                                                  %
                                                </span>
                                                <div className="flex items-center gap-1.5" data-id="element-10272" data-mp="3g6e5gs5ckqxo">
                                                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100" data-id="element-10273" data-mp="3u5dx88o5ply2">
                                                    {m.estimatedLevelCode}
                                                  </span>
                                                  {cito && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${cito.color} ${cito.bgColor} ${cito.borderColor}`} data-id="element-10274" data-mp="3uw2h9axqwu7k">
                                                      {cito.label}
                                                    </span>}
                                                </div>
                                              </div>
                                            </div>;
                          })}
                                      </div>
                                    </div>}
                              </div>;
                  }) : <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-10275" data-mp="15fwqxjcs7wv5">
                            <div className="mb-3" data-id="element-10276" data-mp="2pxp4vptmzycs">
                              <DjayNarrator size="md" emotion="encouraging" data-id="element-10277" data-mp="2kxszpn97e0vn" />
                            </div>
                            <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-10278" data-mp="2gvxz6kz3jrix">
                              Nog geen toetsen
                            </h4>
                            <p className="text-xs text-slate-500 max-w-[220px]" data-id="element-10279" data-mp="2byzaihy7ccs4">
                              {student.name} heeft nog geen toetsen gemaakt. Zet
                              een toets klaar of laat een niveaubepaling doen!
                            </p>
                          </div>}
                      </div>
                    </Card>
                  </motion.div>}

                {activeTab === 'beheer' && <motion.div key="beheer" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-10280" data-mp="3sc66ezph3r0x">
                    <div className="mb-8" data-id="element-10281" data-mp="2qvxgh2ue78fb">
                      <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-10282" data-mp="3en2r93lh7air">
                        <StickyNote className="w-5 h-5 text-indigo-500" data-id="element-10283" data-mp="3jmxmfjcaam9m" />
                        Persoonlijke Notities
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed mb-3" data-id="element-10284" data-mp="35oz8gf1vkrun">
                        Deze notities zijn alleen zichtbaar voor jou. Gebruik
                        dit voor observaties of aandachtspunten.
                      </p>
                      <div className="relative" data-id="element-10285" data-mp="1y9l1lt64nzmf">
                        <textarea value={note} onChange={e => setNote(e.target.value)} onBlur={handleNoteBlur} placeholder="Typ hier je notities over deze leerling..." className="w-full h-32 p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none" data-id="element-10286" data-mp="1y78butu4okyt" />
                        <AnimatePresence data-id="element-10287" data-mp="130r6j9a32pke">
                          {saveStatus === 'saved' && <motion.div initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="absolute bottom-3 right-3 flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md" data-id="element-10288" data-mp="1hpnel6c542l0">
                              <CheckCircle2 className="w-3 h-3" data-id="element-10289" data-mp="363pjzwp4ouf1" />
                              Opgeslagen
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="mb-6" data-id="element-10290" data-mp="3twph9g74swoo">
                      <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-10291" data-mp="3owb97y5j5mqn">
                        Leerlingbeheer
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed" data-id="element-10292" data-mp="3jw6t498jau0y">
                        Beheer de status van deze leerling. Voor het verwijderen
                        of ontkoppelen van leerlingen, ga naar de Klas Beheer
                        pagina.
                      </p>
                    </div>

                    {/* Danger Zone / Beheer */}
                    <div className="space-y-3" data-id="element-10293" data-mp="syu6curv2c5k">
                      {classroomName && onRemoveFromClassroom && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10294" data-mp="xxx0vla7szza">
                          <div data-id="element-10295" data-mp="12qq7yft5okej">
                            <div className="font-bold text-slate-900 text-sm" data-id="element-10296" data-mp="17oje58ugleet">
                              Verwijder uit klas
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-10297" data-mp="1broasuruylvj">
                              Haal {student.name} uit de klas "{classroomName}".
                              De leerling blijft wel gekoppeld aan jouw account.
                            </div>
                          </div>

                          {confirmAction === 'remove' ? <div className="flex items-center gap-2 shrink-0" data-id="element-10298" data-mp="3nfxgf3dhgbll">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-10299" data-mp="3fdoh56j8vepi">
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onRemoveFromClassroom(student.id);
                      setConfirmAction(null);
                    }} data-id="element-10300" data-mp="3kchyja1njpuy">
                                Bevestig
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-slate-600 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200" onClick={() => setConfirmAction('remove')} data-id="element-10301" data-mp="gwswygskz120">
                              <UserMinus className="w-4 h-4 mr-2" data-id="element-10302" data-mp="qjn4ebrbmilr" />
                              Verwijder uit klas
                            </Button>}
                        </div>}

                      {onUnlinkStudent && <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10303" data-mp="lllizk6fa774">
                          <div data-id="element-10304" data-mp="7s4103tdx2j7">
                            <div className="font-bold text-rose-700 text-sm" data-id="element-10305" data-mp="csicjvu72ulz">
                              Ontkoppel leerling
                            </div>
                            <div className="text-xs text-slate-500 mt-1" data-id="element-10306" data-mp="3u387pir0maya">
                              Verbreek de koppeling met {student.name} volledig.
                              Je hebt hierna geen toegang meer tot de voortgang.
                            </div>
                          </div>

                          {confirmAction === 'unlink' ? <div className="flex items-center gap-2 shrink-0" data-id="element-10307" data-mp="wenei6gn2659">
                              <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)} data-id="element-10308" data-mp="rhn8fvjsh3e5">
                                Annuleer
                              </Button>
                              <Button className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" onClick={() => {
                      onUnlinkStudent(student.id);
                      setConfirmAction(null);
                    }} data-id="element-10309" data-mp="di5gx8f3zc13">
                                Bevestig ontkoppeling
                              </Button>
                            </div> : <Button variant="secondary" size="sm" className="shrink-0 text-rose-600 hover:bg-rose-50 border-rose-200" onClick={() => setConfirmAction('unlink')} data-id="element-10310" data-mp="2k5cpq4c6zl5f">
                              <Unlink className="w-4 h-4 mr-2" data-id="element-10311" data-mp="1weyz8i999ucy" />
                              Ontkoppel
                            </Button>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}